import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wand2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CodeEditor, { formatCode } from "./CodeEditor";
import { useEditor, findSection } from "./useEditorState";

function useTarget() {
  const { selection, content } = useEditor();
  if (selection?.kind === "css") return { kind: "css", id: "css", label: "CSS globale", fields: [["global_css", "css", content.global_css || ""]] };
  if (selection?.kind === "data") return { kind: "data", id: "data", label: "Dati simulati (DATA)", fields: [["data", "json", JSON.stringify(content.data ?? {}, null, 2)]] };
  if (selection?.kind === "component") {
    const c = content.components.find((x) => x.id === selection.id);
    if (c) return { kind: "component", id: c.id, label: `Componente ${c.name}`, fields: [["code", "jsx", c.code], ["css", "css", c.css || ""]] };
  }
  if (selection?.kind === "section") {
    const s = findSection(content, selection.id)?.section;
    if (s) return { kind: "section", id: s.id, label: s.name, fields: s.type === "html" ? [["html", "html", s.html], ["css", "css", s.css], ["js", "js", s.js]] : [["code", "jsx", s.code], ["css", "css", s.css || ""]] };
  }
  return null;
}

export default function CodePanel() {
  const ed = useEditor();
  const target = useTarget();
  const [field, setField] = useState(0);
  const [draft, setDraft] = useState("");
  const [jsonErr, setJsonErr] = useState("");
  const pushed = useRef(null);
  const timer = useRef(null);
  const [check, setCheck] = useState(null);
  const runCheck = useCallback(async (code) => {
    const { data } = await ed.api.post("/code/check", { code, project_id: ed.id });
    setCheck(data);
    return data;
  }, [ed.api, ed.id]);
  const f = target?.fields[Math.min(field, target.fields.length - 1)];
  const key = target ? `${target.id}:${f[0]}` : "";

  useEffect(() => { setField(0); setCheck(null); }, [target?.id]);
  useEffect(() => {
    if (!f) return;
    if (pushed.current?.key !== key || pushed.current?.value !== f[2]) { setDraft(f[2]); setJsonErr(""); pushed.current = { key, value: f[2] }; }
  }, [key, f?.[2]]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!target) {
    return (
      <div className="p-6 text-sm text-slate-500 space-y-3" data-testid="code-empty">
        <p>Seleziona una sezione o un componente per modificarne il codice.</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => ed.setSelection({ kind: "css", id: "css" })}>CSS globale</Button>
          <Button size="sm" variant="outline" onClick={() => ed.setSelection({ kind: "data", id: "data" })}>Dati (JSON)</Button>
        </div>
      </div>
    );
  }

  const push = (value) => {
    if (target.kind === "data") {
      try { const parsed = JSON.parse(value); setJsonErr(""); pushed.current = { key, value: JSON.stringify(parsed, null, 2) }; ed.setData(parsed); }
      catch (e) { setJsonErr(e.message); }
      return;
    }
    pushed.current = { key, value };
    if (target.kind === "css") ed.setGlobalCss(value);
    else if (target.kind === "component") ed.updateComponent(target.id, { [f[0]]: value });
    else ed.updateSection(target.id, { [f[0]]: value });
  };
  const onChange = (v) => { setDraft(v); clearTimeout(timer.current); timer.current = setTimeout(() => push(v), 400); };
  const format = async () => {
    try { const out = await formatCode(draft, f[1]); setDraft(out); push(out); toast.success("Codice formattato"); }
    catch (e) { toast.error(`Impossibile formattare: ${String(e.message).split("\n")[0]}`); }
  };
  const checkFailed = f[1] === "jsx" && check && !check.ok;
  // the runtime compile error repeats the server validation one: show it only once
  const errs = ed.diagnostics.filter((d) => d.id === target.id && !(checkFailed && d.type === "compile"));

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="code-panel">
      <div className="px-3 py-2 border-b flex items-center gap-2">
        <Select value={["css", "data"].includes(target.kind) ? target.kind : "sel"} onValueChange={(v) => ed.setSelection(v === "sel" ? null : { kind: v, id: v })}>
          <SelectTrigger className="h-8 text-xs flex-1 min-w-0" data-testid="code-target-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {!["css", "data"].includes(target.kind) && <SelectItem value="sel">{target.label}</SelectItem>}
            <SelectItem value="css">CSS globale</SelectItem>
            <SelectItem value="data">Dati simulati (JSON)</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={format} data-testid="code-format-btn"><Wand2 size={13} className="mr-1" />Formatta</Button>
      </div>
      {target.fields.length > 1 && (
        <div className="flex border-b">
          {target.fields.map((x, i) => (
            <button key={x[0]} onClick={() => setField(i)} data-testid={`code-field-${x[0]}`}
              className={`px-4 py-2 text-xs font-mono uppercase ${i === field ? "border-b-2 border-sky-600 text-sky-700" : "text-slate-500"}`}>{x[0]}</button>
          ))}
        </div>
      )}
      {f[0] === "css" && target.kind !== "css" && <p className="px-3 py-1.5 text-[11px] text-slate-500 border-b bg-slate-50" data-testid="local-css-hint">CSS locale: vale solo per questo elemento (i selettori vengono limitati automaticamente). Usa <code>:scope</code> per il contenitore.</p>}
      <div className="flex-1 min-h-0"><CodeEditor value={draft} onChange={onChange} lang={f[1]} testId="code-editor" check={f[1] === "jsx" ? runCheck : undefined} /></div>
      <div className="border-t px-3 py-2 max-h-40 overflow-auto" data-testid="code-status">
        {jsonErr && <p className="text-xs text-red-600 flex gap-1.5"><AlertTriangle size={13} className="shrink-0 mt-0.5" />JSON non valido: {jsonErr}</p>}
        {checkFailed && <pre className="text-[11px] text-red-700 whitespace-pre-wrap font-mono" data-testid="code-check-error">Validazione{check.line ? ` (riga ${check.line})` : ""}: {check.error}</pre>}
        {errs.map((d) => <pre key={d.type} className="text-[11px] text-red-700 whitespace-pre-wrap font-mono">{d.message}</pre>)}
        {!jsonErr && !errs.length && !checkFailed && <p data-testid="code-ok" className="text-[11px] text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={13} />Nessun errore · le modifiche si vedono subito nell'anteprima</p>}
      </div>
    </div>
  );
}
